import { useState, useEffect, useRef } from 'react';
function checkForErrors(response) {
    if (!response.ok) {
        throw Error(response.statusText ||
            `useApi | An api call returned HTTP status ${response.status}`);
    }
    return response;
}
export const callApi = (url, fetchOptions) => {
    return fetch(url, fetchOptions)
        .then(checkForErrors)
        .then((response) => response.json());
};
/**
 * @description
 * A custom hook to make a GET request using the given url
 * returning { loading, error, data }
 * @param {String} url - The url to fetch
 * @param {Object} options
 * @param {Number} options.method - If supplied, the api will be called using this value
 * @param {Number} options.headers - If supplied, the api will be called using this value
 * @param {Number} options.body - If supplied, the api will be called using this value
 * @param {Number} options.credentials - If supplied, the api will be called using this value
 * @param {Number} options.pollInterval - If supplied, the api will be polled at this interval
 * */
export const useApi = (url, options) => {
    const [request, setRequest] = useState({
        loading: true,
    });
    const alreadyFetched = useRef(false);
    const pollRef = useRef();
    useEffect(() => {
        const fetchOptions = {
            method: options?.method || 'GET',
            credentials: options?.credentials,
            headers: options?.headers,
            body: options?.body,
        };
        const handleData = (data) => {
            setRequest({
                data,
                loading: false,
            });
        };
        const handleError = (error) => {
            setRequest({
                error,
                loading: false,
            });
        };
        const poll = (delay) => {
            // We use window.setTimeout so we're sure to get a number back (and not the NodeJS.Timeout type)
            const timeoutId = window.setTimeout(() => {
                callApi(url, fetchOptions)
                    .then(handleData)
                    .then(() => {
                    // If successfull, enqueue the next poll
                    poll(delay);
                })
                    .catch(handleError);
            }, delay);
            pollRef.current = timeoutId;
        };
        if (alreadyFetched.current === false) {
            callApi(url, fetchOptions).then(handleData).catch(handleError);
            alreadyFetched.current = true;
        }
        if (options?.pollInterval) {
            const delay = options.pollInterval;
            poll(delay);
            return () => {
                if (pollRef.current) {
                    clearTimeout(pollRef.current);
                    pollRef.current = undefined;
                }
            };
        }
    }, [url, options]);
    return request;
};
